import { faEnvelope, faGlobe, faPhone, faWarning } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, Card, Col, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatDate } from "../services/i18n";
import type {
  CampsiteDataFragment,
  CampsiteOwnerDataFragment,
  ReportDataFragment,
  SeasonDataFragment,
} from "../types/graphql/graphql";
import CampsiteCalendar from "./CampsiteCalendar";
import { ReportItem } from "./ReportItem";

type CampsiteAvailabilityProps = {
  campsite: CampsiteDataFragment;
  owner?: CampsiteOwnerDataFragment;
  reports: ReportDataFragment[];
  onReport: () => void;
  seasons: SeasonDataFragment[];
  campsiteId: CampsiteDataFragment["id"];
};

export function CampsiteAvailability({
  campsite,
  owner,
  onReport,
  reports,
  seasons,
  campsiteId,
}: CampsiteAvailabilityProps) {
  const { addressLines, email, homepage, phone1, phone2 } = owner ?? {};
  const { t } = useTranslation();
  const occupationReports = reports.filter(({ type }) => type === "OCCUPIED");

  return (
    <>
      {campsite.calendarManagement !== "full" && (
        <Alert variant="warning" className="mb-0 rounded-0">
          <p>{t("public.campsites.tabs.report.hint")}</p>
          <div className="text-end mt-2">
            <Button onClick={onReport} variant="primary">
              <FontAwesomeIcon icon={faWarning} /> {t("public.campsites.tabs.report.add_report")}
            </Button>
          </div>
        </Alert>
      )}
      {campsite.calendarManagement === "full" && (
        <Card.Body className="bg-white-50">
          <CampsiteCalendar campsiteId={campsiteId} />
        </Card.Body>
      )}
      <Card.Body>
        <Row>
          {owner && (
            <Col md={5} className="offset-md-2">
              <Card className="shadow-sm bg-secondary" style={{ backgroundColor: "wheat" }}>
                <Card.Body>
                  <h6 className="mb-2 fw-bold">{t("activerecord.models.campsite_owner.one")}</h6>
                  <address className="white-space-pre-line m-0 p-0">{addressLines?.join("\n")}</address>
                </Card.Body>
                <Card.Footer>
                  <Stack direction="horizontal" gap={2} className="flex-wrap text-nowrap">
                    {email && (
                      <a href={`mailto:${email}`}>
                        <FontAwesomeIcon icon={faEnvelope} /> {email}
                      </a>
                    )}
                    {phone1 && (
                      <a href={`tel:${phone1?.replace(" ", "")}`}>
                        <FontAwesomeIcon icon={faPhone} /> {phone1}
                      </a>
                    )}
                    {phone2 && (
                      <a href={`tel:${phone2?.replace(" ", "")}`}>
                        <FontAwesomeIcon icon={faPhone} /> {phone2}
                      </a>
                    )}
                    {homepage && (
                      <a href={homepage} className="text-truncate">
                        <FontAwesomeIcon icon={faGlobe} /> {homepage}
                      </a>
                    )}
                  </Stack>
                </Card.Footer>
              </Card>
            </Col>
          )}
          <Col md={5} className="order-first">
            {seasons.length > 0 && (
              <Stack>
                {seasons.map((season) => (
                  <div key={season.id} className="bg-white-50 rounded p-3 mb-3">
                    <div className="d-flex align-items-baseline gap-2 fw-bold">
                      <span>
                        {t("activerecord.models.season.one")} {season.name}
                      </span>
                      <small className="text-muted">
                        {season.beginsAt && formatDate(season.beginsAt)} – {season.endsAt && formatDate(season.endsAt)}
                      </small>
                    </div>
                    <p className="mb-0">{season.description}</p>
                  </div>
                ))}
              </Stack>
            )}
            {campsite.calendarManagement !== "full" &&
              occupationReports.map((report) => <ReportItem key={report.id} report={report} />)}
          </Col>
        </Row>
      </Card.Body>
    </>
  );
}
